'use client';

import {cva} from 'class-variance-authority';
import {useCallback, useRef, useState} from 'react';

import {cn} from '../../lib/utils';
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '../shadcn/tooltip';

import type {VariantProps} from 'class-variance-authority';
import type {HTMLAttributes} from 'react';

export const typographyVariantsConfig = {
  variants: {
    size: {
      'display-xl': 'text-display-xl leading-display-xl tracking-display font-display',
      'display-lg': 'text-display-lg leading-display-lg tracking-display font-display',
      'display-md': 'text-display-md leading-display-md tracking-display font-display',
      'display-sm': 'text-display-sm leading-display-sm font-display',
      'display-xs': 'text-display-xs leading-display-xs font-display',
      'display-2xs': 'text-display-2xs leading-display-2xs font-display',
      xl: 'text-xl leading-body-xl font-body',
      lg: 'text-lg leading-body-lg font-body',
      md: 'text-base leading-body-md font-body',
      sm: 'text-sm leading-body-sm font-body',
      xs: 'text-xs leading-body-xs font-body'
    },
    weight: {
      regular: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold'
    },
    color: {
      default: 'text-fg',
      secondary: 'text-fg-secondary',
      tertiary: 'text-fg-tertiary',
      disabled: 'text-fg-disabled',
      'on-brand': 'text-fg_on-brand'
    }
  },
  defaultVariants: {
    size: 'md',
    weight: 'regular',
    color: 'default'
  }
} as const;

export const typographyVariants = cva('truncate', typographyVariantsConfig);

type TypographyElement = 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'label' | 'div';

export interface TypographyProps
  extends VariantProps<typeof typographyVariants>, Omit<HTMLAttributes<HTMLElement>, 'color'> {
  as?: TypographyElement;
}

export function Typography({
  as: Tag = 'p',
  size,
  weight,
  color,
  className,
  children,
  ...htmlProps
}: TypographyProps) {
  const elementRef = useRef<HTMLElement | null>(null);
  const [tooltipText, setTooltipText] = useState('');

  const handleMouseEnter = useCallback(() => {
    const el = elementRef.current;
    if (!el) return;
    setTooltipText(el.scrollWidth > el.clientWidth ? (el.textContent ?? '') : '');
  }, []);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <Tag
              {...(htmlProps as object)}
              ref={elementRef as any}
              onMouseEnter={handleMouseEnter}
              data-slot="typography"
              className={cn(typographyVariants({size, weight, color}), className)}
            />
          }
        >
          {children}
        </TooltipTrigger>
        {tooltipText && <TooltipContent>{tooltipText}</TooltipContent>}
      </Tooltip>
    </TooltipProvider>
  );
}
